import { ChevronLeft, ChevronRight } from "lucide-react"
import type { ComponentProps } from "react"
import { format } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { DayPicker } from "react-day-picker"
import { cn } from "@/shared/lib/utils"

/**
 * Подписи для экранного диктора. Локаль react-day-picker не берём: её модуль тянет все
 * локали date-fns, и dev-сервер упирается в лимит открытых файлов.
 */
const LABELS = {
	labelNext: () => "Следующий месяц",
	labelPrevious: () => "Предыдущий месяц",
	labelDayButton: (date: Date, modifiers: { today?: boolean; selected?: boolean }) =>
		`${modifiers.today ? "Сегодня, " : ""}${format(date, "EEEE, d MMMM yyyy", { locale: ru })}${modifiers.selected ? ", выбрано" : ""}`,
}

/** Календарь в стиле панели: неделя с понедельника, русские названия, крупные цели для клика. */
export function Calendar({ className, classNames, ...props }: ComponentProps<typeof DayPicker>) {
	return (
		<DayPicker
			locale={ru}
			labels={LABELS}
			weekStartsOn={1}
			showOutsideDays
			className={cn("p-3", className)}
			classNames={{
				months: "relative flex flex-col",
				month: "space-y-3",
				month_caption: "flex h-9 items-center justify-center",
				caption_label: "text-sm font-semibold capitalize",
				nav: "absolute inset-x-0 top-0 flex h-9 items-center justify-between",
				button_previous:
					"grid size-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-30",
				button_next:
					"grid size-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-30",
				month_grid: "w-full border-collapse",
				weekdays: "flex",
				weekday: "w-10 text-center text-[11px] font-medium text-muted-foreground uppercase",
				week: "mt-1 flex",
				day: "size-10 p-0 text-center text-sm",
				day_button:
					"size-10 rounded-lg tabular-nums transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
				selected:
					"[&>button]:bg-primary [&>button]:font-semibold [&>button]:text-primary-foreground [&>button]:hover:bg-primary",
				today: "[&>button]:ring-1 [&>button]:ring-primary/50",
				outside: "text-muted-foreground/40",
				disabled: "text-muted-foreground/30 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent",
				hidden: "invisible",
				...classNames,
			}}
			components={{
				Chevron: ({ orientation }) =>
					orientation === "left" ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />,
			}}
			{...props}
		/>
	)
}
